/* ============================================================================
 * WIDE — Simulateur bureaux : MOTEUR DE CALCUL
 * ----------------------------------------------------------------------------
 * Fonction pure : mêmes entrées → mêmes sorties. Aucun accès réseau, aucun
 * accès au DOM, aucune dépendance. Testable et rejouable tel quel.
 *
 * Principe : plutôt que des formules fermées, on construit un ÉCHÉANCIER
 * ANNUEL par option. C'est ce qui permet d'indexer et d'actualiser sans
 * multiplier les formules spéciales.
 *
 * L'effectif est CONSTANT sur toute la projection. Un scénario de croissance
 * a été implémenté puis retiré volontairement : il chiffrait le déménagement
 * et la revente-rachat, mais laissait de côté l'essentiel — friction,
 * immobilisation des équipes, plateau introuvable au bon moment — qui n'est
 * pas quantifiable sérieusement. La flexibilité se défend en argument
 * commercial, pas en euros. Le moteur qui la calculait est conservé dans
 * archive-engine-avec-croissance.js.
 * ========================================================================= */

/* --------------------------------------------------------------------------
 * Table effectif → surface privative.
 * Reprise telle quelle de la feuille « RETOUR L S - AJUSTEMENT » à partir de
 * son premier palier officiel (4 personnes = 80 m²).
 * ----------------------------------------------------------------------- */
const SURFACE_TABLE = [
  [4, 80], [8, 130], [12, 180], [16, 240], [20, 300], [24, 360],
  [28, 410], [32, 460], [36, 520], [40, 580], [44, 630], [48, 680], [50, 720],
];

// En dessous du premier palier officiel, la table aplatirait tout le monde
// sur 80 m² — un bureau individuel n'a pas besoin d'un plateau prévu pour 4.
// On interpole donc linéairement entre un bureau seul (1 personne, ~25 m²)
// et ce premier palier, plutôt que d'inventer d'autres paliers arbitraires.
const SMALL_OFFICE_MIN_SURFACE = 25;

/** Surface privative nécessaire pour un effectif donné (palier supérieur). */
function surfaceForCollaborators(n) {
  const [firstCollab, firstSurface] = SURFACE_TABLE[0];
  if (n <= firstCollab) {
    if (n <= 1) return SMALL_OFFICE_MIN_SURFACE;
    const t = (n - 1) / (firstCollab - 1);
    return Math.round(SMALL_OFFICE_MIN_SURFACE + t * (firstSurface - SMALL_OFFICE_MIN_SURFACE));
  }
  for (const [collab, surface] of SURFACE_TABLE) if (n <= collab) return surface;
  // Au-delà de 50 personnes, extrapolation linéaire sur la pente de fin de table.
  const [lastC, lastS] = SURFACE_TABLE[SURFACE_TABLE.length - 1];
  const [prevC, prevS] = SURFACE_TABLE[SURFACE_TABLE.length - 2];
  const slope = (lastS - prevS) / (lastC - prevC);
  return Math.round(lastS + (n - lastC) * slope);
}

/** Effectif tenable dans une surface donnée (réciproque, palier inférieur). */
function collaboratorsForSurface(m2) {
  let best = SURFACE_TABLE[0];
  for (const entry of SURFACE_TABLE) if (entry[1] <= m2) best = entry;
  return best[0];
}

/* --------------------------------------------------------------------------
 * Mathématiques financières
 * ----------------------------------------------------------------------- */

/** Mensualité d'un prêt amortissable (annuité constante). */
function monthlyPayment(principal, annualRatePct, years) {
  if (principal <= 0 || years <= 0) return 0;
  const i = annualRatePct / 100 / 12;
  const n = Math.round(years * 12);
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

/** Capital restant dû après `elapsedYears` années. */
function remainingBalance(principal, annualRatePct, years, elapsedYears) {
  if (principal <= 0) return 0;
  const i = annualRatePct / 100 / 12;
  const n = Math.round(years * 12);
  const k = Math.min(Math.round(elapsedYears * 12), n);
  if (i === 0) return principal * (1 - k / n);
  const pmt = monthlyPayment(principal, annualRatePct, years);
  return principal * Math.pow(1 + i, k) - pmt * ((Math.pow(1 + i, k) - 1) / i);
}

/** Part d'intérêts payée pendant l'année `year` (année 0 = première année). */
function interestPaidInYear(principal, annualRatePct, years, year) {
  if (principal <= 0 || year >= years) return 0;
  const startBal = remainingBalance(principal, annualRatePct, years, year);
  const endBal = remainingBalance(principal, annualRatePct, years, year + 1);
  const paid = monthlyPayment(principal, annualRatePct, years) * 12;
  return paid - (startBal - endBal);
}

/** Facteur d'indexation composée après `year` années. */
const idx = (rate, year) => Math.pow(1 + rate, year);

/* --------------------------------------------------------------------------
 * Agrégation d'un échéancier
 * ----------------------------------------------------------------------- */

function emptyTotals() {
  return { nominal: 0, discounted: 0 };
}

/**
 * `timeline` : [{ year, flows: { poste: montant, ... } }]
 * Retourne les totaux par poste, le total général, et la version actualisée.
 */
function aggregate(timeline, discountRate) {
  const byLine = {};
  const totals = emptyTotals();
  for (const step of timeline) {
    const df = 1 / Math.pow(1 + discountRate, step.year);
    for (const [line, amount] of Object.entries(step.flows)) {
      if (!byLine[line]) byLine[line] = emptyTotals();
      byLine[line].nominal += amount;
      byLine[line].discounted += amount * df;
      totals.nominal += amount;
      totals.discounted += amount * df;
    }
  }
  return { byLine, totals };
}

/* --------------------------------------------------------------------------
 * OPTION 1 — Bail commercial 3/6/9
 * ----------------------------------------------------------------------- */

function simulateRental(input, cfg) {
  const g = cfg.global;
  const loc = cfg.locality;
  const H = input.horizon;
  const rentPerSqm = input.rentPerSqmYear;

  const timeline = [];
  const events = [];
  const currentSurface = surfaceForCollaborators(input.collaborators);
  let lastFitOutYear = 0;
  let depositPaid = 0;

  for (let y = 0; y < H; y++) {
    const flows = {};
    const rentIdxF = idx(g.rentIndexation, y);

    // --- Installation initiale -------------------------------------------
    if (y === 0) {
      flows.amenagement = (flows.amenagement || 0) + (g.furniturePerSqm + g.worksPerSqm) * currentSurface;
      const rentYear0 = currentSurface * rentPerSqm;
      flows.honorairesAgence = (flows.honorairesAgence || 0) + rentYear0 * g.agencyFeesRateRental;
      depositPaid = (rentYear0 / 12) * g.depositMonths;
    }

    // --- Rafraîchissement en fin de vie de l'aménagement ------------------
    if (y > 0 && y - lastFitOutYear >= g.fitOutLifespanYears) {
      flows.rafraichissement =
        (g.furniturePerSqm + g.worksPerSqm) * currentSurface * g.fitOutRenewalRate;
      lastFitOutYear = y;
      events.push({ year: y, type: 'rafraichissement', surface: currentSurface });
    }

    // --- Récurrent --------------------------------------------------------
    flows.loyer = currentSurface * rentPerSqm * rentIdxF;
    // Deux lignes distinctes : ce qui relève de l'immeuble, et ce qui relève
    // des services aux occupants. Le total est le même, mais il devient
    // auditable — un lecteur peut confronter la première au marché.
    flows.chargesImmobilieres =
      currentSurface * loc.chargesImmobilieresPerSqmMonthly * 12 * idx(g.chargesIndexation, y);
    flows.servicesOccupants =
      currentSurface * loc.servicesOccupantsPerSqmMonthly * 12 * idx(g.chargesIndexation, y);
    flows.taxeFonciere = currentSurface * loc.propertyTaxPerSqmYear * idx(g.propertyTaxIndexation, y);

    timeline.push({ year: y, surface: currentSurface, collaborators: input.collaborators, flows });
  }

  const agg = aggregate(timeline, g.discountRate);

  return {
    option: 'location',
    label: 'Bail commercial 3/6/9',
    timeline,
    events,
    lines: agg.byLine,
    total: agg.totals,
    // La caution est restituée : affichée, mais hors coût.
    refundable: { caution: depositPaid },
    residualValue: 0,
    net: { nominal: agg.totals.nominal, discounted: agg.totals.discounted },
    finalSurface: currentSurface,
  };
}

/* --------------------------------------------------------------------------
 * OPTION 2 — Achat
 * ----------------------------------------------------------------------- */

function simulateBuy(input, cfg) {
  const g = cfg.global;
  const loc = cfg.locality;
  const H = input.horizon;
  const pricePerSqm = input.propertyPricePerSqm;
  const rate = input.interestRate;
  const dpRate = input.downPaymentRate;
  const loanYears = input.loanTermYears || H;
  // Ancien : DMTO + émoluments, de l'ordre de 7 à 8,5 % du prix.
  // Neuf ou vendeur assujetti : droits réduits, mais TVA 20 % sur le prix —
  // récupérable pour une entreprise assujettie, d'où son exclusion du coût.
  const notaryRate =
    input.acquisitionRegime === 'neuf' ? g.notaryFeesRateNeuf : g.notaryFeesRateAncien;

  const timeline = [];
  const events = [];

  const currentSurface = surfaceForCollaborators(input.collaborators);
  const purchasePrice = currentSurface * pricePerSqm;
  const purchaseYear = 0;
  const loanPrincipal = purchasePrice * (1 - dpRate);
  const loanStartYear = 0;
  let lastFitOutYear = 0;

  const acquisitionFlows = (surface, price, year, flows) => {
    // Aménagement payé comptant : aucune banque n'amortit du mobilier sur
    // la durée d'un prêt immobilier, contrairement au modèle précédent.
    flows.apport = price * dpRate;
    flows.fraisNotaire = price * notaryRate;                 // assis sur le PRIX
    flows.honorairesAgence = price * g.agencyFeesRate;
    flows.fraisDossierBancaire = price * (1 - dpRate) * g.bankFeesRate;
    flows.fraisJuridiques = year === 0 ? g.legalFeesFixed : 0;
    flows.amenagement = (g.furniturePerSqm + g.worksPerSqm) * surface;
  };

  for (let y = 0; y < H; y++) {
    const flows = {};

    if (y === 0) {
      acquisitionFlows(currentSurface, purchasePrice, 0, flows);
      lastFitOutYear = 0;
    }

    // --- Rafraîchissement --------------------------------------------------
    if (y > 0 && y - lastFitOutYear >= g.fitOutLifespanYears) {
      flows.rafraichissement =
        (g.furniturePerSqm + g.worksPerSqm) * currentSurface * g.fitOutRenewalRate;
      lastFitOutYear = y;
      events.push({ year: y, type: 'rafraichissement', surface: currentSurface });
    }

    // --- Service de la dette ------------------------------------------------
    const yearInLoan = y - loanStartYear;
    if (yearInLoan >= 0 && yearInLoan < loanYears) {
      const annual = monthlyPayment(loanPrincipal, rate, loanYears) * 12;
      const interest = interestPaidInYear(loanPrincipal, rate, loanYears, yearInLoan);
      flows.interetsBancaires = interest;
      flows.remboursementCapital = annual - interest;
    }

    // --- Récurrent ----------------------------------------------------------
    // Deux lignes distinctes : ce qui relève de l'immeuble, et ce qui relève
    // des services aux occupants. Le total est le même, mais il devient
    // auditable — un lecteur peut confronter la première au marché.
    flows.chargesImmobilieres =
      currentSurface * loc.chargesImmobilieresPerSqmMonthly * 12 * idx(g.chargesIndexation, y);
    flows.servicesOccupants =
      currentSurface * loc.servicesOccupantsPerSqmMonthly * 12 * idx(g.chargesIndexation, y);
    flows.taxeFonciere = currentSurface * loc.propertyTaxPerSqmYear * idx(g.propertyTaxIndexation, y);
    flows.grosEntretien = currentSurface * g.ownerMaintenancePerSqmYear * idx(g.chargesIndexation, y);
    flows.fraisDeGestion = g.managementFeesAnnual;

    timeline.push({ year: y, surface: currentSurface, collaborators: input.collaborators, flows });
  }

  const agg = aggregate(timeline, g.discountRate);

  // --- Valeur résiduelle en fin d'horizon ---------------------------------
  const elapsed = H - purchaseYear;
  const marketValue = purchasePrice * idx(g.propertyAppreciation, elapsed);
  const netSaleValue = marketValue * (1 - g.sellingCostRate);
  const outstanding = remainingBalance(loanPrincipal, rate, loanYears, H - loanStartYear);
  const residual = netSaleValue - outstanding;
  const residualDiscounted = residual / Math.pow(1 + g.discountRate, H);

  return {
    option: 'achat',
    label: 'Achat',
    timeline,
    events,
    lines: agg.byLine,
    total: agg.totals,
    refundable: {},
    residualValue: residual,
    residualDetail: { marketValue, netSaleValue, outstandingLoan: outstanding },
    // Coût brut : toutes sorties de trésorerie, capital compris.
    // Coût net : déduction faite de ce que vaut le bien au terme.
    net: {
      nominal: agg.totals.nominal - residual,
      discounted: agg.totals.discounted - residualDiscounted,
    },
    finalSurface: currentSurface,
  };
}

/* --------------------------------------------------------------------------
 * OPTION 3 — WIDE
 * ----------------------------------------------------------------------- */

function simulateWide(input, cfg) {
  const g = cfg.global;
  const loc = cfg.locality;
  const H = input.horizon;
  const n = input.collaborators;

  const timeline = [];
  let depositPaid = 0;

  for (let y = 0; y < H; y++) {
    const flows = {};
    const monthly = n * loc.wideWorkstationPrice * idx(g.wideIndexation, y);
    flows.forfaitWide = monthly * 12;
    if (y === 0) depositPaid = n * loc.wideWorkstationPrice * loc.wideDepositMonths;

    const privative = surfaceForCollaborators(n);
    timeline.push({
      year: y,
      surface: privative + Math.round(privative * loc.wideSharedSurfaceRate),
      collaborators: n,
      flows,
    });
  }

  const agg = aggregate(timeline, g.discountRate);
  const lastPrivative = surfaceForCollaborators(n);

  return {
    option: 'wide',
    label: 'WIDE — tout inclus',
    timeline,
    events: [],
    lines: agg.byLine,
    total: agg.totals,
    refundable: { caution: depositPaid },
    residualValue: 0,
    net: { nominal: agg.totals.nominal, discounted: agg.totals.discounted },
    finalSurface: lastPrivative + Math.round(lastPrivative * loc.wideSharedSurfaceRate),
    availableHere: loc.wideAvailable,
    site: loc.wideSite,
  };
}

/* --------------------------------------------------------------------------
 * Point d'entrée
 * ----------------------------------------------------------------------- */

/**
 * @param {object} input
 *   localityId, collaborators, horizon,
 *   rentPerSqmYear, propertyPricePerSqm, downPaymentRate, interestRate,
 *   loanTermYears?, acquisitionRegime?
 * @param {object} configModule  le module config.js
 */
function simulate(input, configModule) {
  const cfg = configModule.resolveConfig(input.localityId);
  const loc = cfg.locality;

  const filled = {
    horizon: input.horizon,
    collaborators: input.collaborators,
    rentPerSqmYear: input.rentPerSqmYear != null ? input.rentPerSqmYear : loc.rentPerSqmYear,
    propertyPricePerSqm:
      input.propertyPricePerSqm != null ? input.propertyPricePerSqm : loc.propertyPricePerSqm,
    downPaymentRate:
      input.downPaymentRate != null ? input.downPaymentRate : cfg.ui.downPaymentRateDefault,
    interestRate: input.interestRate != null ? input.interestRate : cfg.ui.interestRateDefault,
    loanTermYears: input.loanTermYears || null,
    acquisitionRegime: input.acquisitionRegime || 'ancien',
  };

  const rental = simulateRental(filled, cfg);
  const buy = simulateBuy(filled, cfg);
  const wide = simulateWide(filled, cfg);

  // Coût annuel par collaborateur : la métrique la plus parlante pour un
  // dirigeant, et la seule qui reste comparable entre localités.
  const collabYears = filled.collaborators * filled.horizon;
  const perCollab = (opt, useNet) =>
    collabYears ? (useNet ? opt.net.nominal : opt.total.nominal) / collabYears : 0;

  const impliedYield = filled.rentPerSqmYear / filled.propertyPricePerSqm;

  return {
    input: filled,
    locality: { id: loc.id, name: loc.name, wideAvailable: loc.wideAvailable, wideSite: loc.wideSite,
                distanceToWideSiteKm: loc.distanceToWideSiteKm, travelTimeToWideSite: loc.travelTimeToWideSite },
    options: { location: rental, achat: buy, wide },
    perCollaboratorPerYear: {
      location: perCollab(rental, false),
      achatBrut: perCollab(buy, false),
      achatNet: perCollab(buy, true),
      wide: perCollab(wide, false),
    },
    checks: {
      impliedYield,
      impliedYieldWarning:
        impliedYield < cfg.ui.impliedYieldWarnBelow || impliedYield > cfg.ui.impliedYieldWarnAbove,
      wideIsElsewhere: !loc.wideAvailable,
      horizonSharedByAllOptions: true,
    },
    config: cfg,
  };
}

const ENGINE = {
  simulate,
  simulateRental,
  simulateBuy,
  simulateWide,
  surfaceForCollaborators,
  collaboratorsForSurface,
  monthlyPayment,
  remainingBalance,
  interestPaidInYear,
  aggregate,
  SURFACE_TABLE,
};

if (typeof module !== 'undefined' && module.exports) module.exports = ENGINE;
if (typeof window !== 'undefined') window.WIDE_ENGINE = ENGINE;
